/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import {
  Text,
  Space,
  Conditional,
  Toggle,
  Popup,
  Button,
  Dropdown,
  AlertModal,
  ConfirmModal,
} from '@cashfree-intl/coherent';
import get from 'lodash/get';

import CustomIcon from 'components/CustomIcons';

import {
  CURRENCY_SYMBOL,
  PGDB_SHOW_PAYPAL_FOREX_MARKUP,
  PLACEHOLDER_TEXT,
} from 'utils/constants';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';
import { titleCase } from 'utils/common';

import { MerchantContext } from 'contexts/MerchantContext';
import { PAYPAL_CONFIG } from '../../constants';
import ForexMarkup from './ForexMarkup';

import {
  ENABLE_FOREX_MARKUP,
  DISABLE_FOREX_MARKUP,
  DEFAULT_CON_FEE,
  DEFAULT_FOREX_INPUT_VALUE,
} from './constants';

const Paypal = ({
  data,
  isTest,
  onToggleClick,
  activatePaypalCurrency,
  deactivatePaypalCurrency,
  updateDefaultPaypalCurrency,
  updatePaypalConfig,
  paypalRedirectionSucess,
}) => {
  const accountStatus = data?.paypalAccount?.active ? 'ACTIVE' : '';
  const isAccountActive = accountStatus === 'ACTIVE';

  if (paypalRedirectionSucess && isAccountActive) {
    const forexInputValue = localStorage.getItem('forexMarkup');
    if (forexInputValue) {
      updatePaypalConfig({ markup: forexInputValue, enableForexMarkup: true });
      localStorage.removeItem('forexMarkup');
    }
  }

  const isForexMarkupEnabled = get(
    data,
    'paypalAccount.forexMarkupEnabled',
    false,
  );
  const conFee = get(data, 'paypalAccount.conFee', DEFAULT_CON_FEE);

  const { featureToggles = {} } = useContext(MerchantContext);

  const [expand, resetExpand] = useState(false);
  const [editKey, resetEditKey] = useState([]);

  const [selectedDefaultCurrency, resetDefaultCurrency] = useState('');
  const [selectedActiveCurrency, resetActiveCurrency] = useState('');
  const [ctaLoader, resetCtaLoader] = useState(false);
  const [openModal, setModal] = useState('');
  const [forexInputValue, setForexInputValue] = useState('');

  const showForexMarkup =
    PGDB_SHOW_PAYPAL_FOREX_MARKUP in featureToggles &&
    featureToggles[PGDB_SHOW_PAYPAL_FOREX_MARKUP];

  useEffect(() => {
    setForexInputValue(conFee || DEFAULT_FOREX_INPUT_VALUE);
  }, [conFee]);

  useEffect(() => {
    resetExpand(!!accountStatus);
  }, [accountStatus]);

  const onMarkupSubmit = () => {
    if (isAccountActive) {
      setModal(
        isForexMarkupEnabled ? DISABLE_FOREX_MARKUP : ENABLE_FOREX_MARKUP,
      );
    } else {
      localStorage.setItem('forexMarkup', forexInputValue);
      // show the connect account modal
      onToggleClick(isAccountActive);
    }
  };

  const onMarkupUpdate = () => {
    if (isAccountActive) setModal(ENABLE_FOREX_MARKUP);
    else {
      localStorage.setItem('forexMarkup', forexInputValue);
      onToggleClick(isAccountActive);
    }
  };

  const updateForexMarkupStatus = enable => () => {
    setModal('');
    updatePaypalConfig({ markup: forexInputValue, enableForexMarkup: enable });
  };

  const onForexMarkupModalClose = () => setModal('');

  // eslint-disable-next-line no-unused-vars
  const onToggle = e => {
    e.stopPropagation();
    // check here if we need to active or deactivate
    onToggleClick(isAccountActive);
    Analytics.trackEventWithNewNomenclature({
      eventName: 'Form_Paypal_Toggle',
      eventProperties: {
        active: accountStatus !== 'ACTIVE',
      },
    });
  };

  const onChevronClick = () => resetExpand(!expand);

  const handleEditCTAClick = key => () => {
    resetEditKey([...editKey, key]);
  };

  const onDefaultCurrencyChange = (e, { value }) => resetDefaultCurrency(value);

  const onActiveCurrencyChange = (e, { value }) => resetActiveCurrency(value);

  const handleConfirmClick = key => () => {
    const obj = {
      successCb: () => {
        resetCtaLoader('');
        const arr = editKey.filter(elem => elem !== key);
        resetEditKey(arr);
        if (key === 'defaultPaypalCurrency') resetDefaultCurrency('');
        else resetActiveCurrency('');
      },
      errorCb: () => resetCtaLoader(''),
    };

    if (key === 'defaultPaypalCurrency') {
      resetCtaLoader(key);
      updateDefaultPaypalCurrency({
        ...obj,
        currencyCode: selectedDefaultCurrency || data.defaultPaypalCurrency,
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.DEFAULT_CURRENCY, {
        currencyCode: selectedDefaultCurrency || data.defaultPaypalCurrency,
      });
    } else if (selectedActiveCurrency) {
      resetCtaLoader(key);
      activatePaypalCurrency({ ...obj, currencyCode: selectedActiveCurrency });
      Analytics.track(EVENTS.PAYMENT_METHODS.ACTIVE_CURRENCY, {
        currencyCode: [...data.activePaypalCurrencies, selectedActiveCurrency],
      });
    } else {
      Analytics.track(EVENTS.PAYMENT_METHODS.ACTIVE_CURRENCY, {
        currencyCode: data.activePaypalCurrencies,
      });
    }
  };

  const deactivateCurrency = currency => () => {
    // enableLoader();
    deactivatePaypalCurrency({ currencyCode: currency });
    Analytics.track(EVENTS.PAYMENT_METHODS.DEACTIVATE_PAYPAL_CURRENCY, {
      currencyCode: currency,
    });
  };

  const getDropdownOptions = type =>
    data.paypalCurrencies.filter(obj => {
      if (type === 'defaultPaypalCurrency') return true;
      return !data.activePaypalCurrencies.includes(obj.value);
    });

  const renderAccountDetails = () => {
    const lookupObj = {
      ...data.paypalAccount,
      activePaypalCurrencies:
        data.activePaypalCurrencies && data.activePaypalCurrencies.join(', '),
      defaultPaypalCurrency: data.defaultPaypalCurrency,
    };

    return PAYPAL_CONFIG.map(obj => (
      <div className="paypal-config-details mb-2" key={obj.key}>
        <Space alignItems="center">
          <Text color="bodyLight">{obj.displayText}</Text>
          <Conditional if={obj.showInfoIcon}>
            <Popup
              key={obj.key}
              content={`Popup content for ${obj.displayText}`}
              position="right center"
              trigger={
                <span className="icon pointer ml-1">
                  <CustomIcon componentName="INFO" />
                </span>
              }
            />
          </Conditional>
        </Space>
        <Space alignItems="center" gap={3}>
          <Space alignItems="center">
            <Conditional if={!editKey.includes(obj.key)}>
              <Space alignItems="center" gap={2}>
                <Text>{`${
                  obj.isAmount && typeof lookupObj[obj.key] === 'number'
                    ? CURRENCY_SYMBOL.INR
                    : ''
                } ${
                  obj.isAmount && typeof lookupObj[obj.key] === 'number'
                    ? lookupObj[obj.key] || 0
                    : lookupObj[obj.key] || PLACEHOLDER_TEXT
                }`}</Text>
                <Conditional
                  if={editKey !== obj.key && obj.isEditable && !isTest}
                >
                  <span
                    data-testid="edit-cta"
                    className="edit-cta pointer"
                    onClick={handleEditCTAClick(obj.key)}
                    data-event-name="Form_Paypal_Icon_Edit"
                    data-ignore-inner-text="true"
                  >
                    <CustomIcon componentName="EDIT" />
                  </span>
                </Conditional>
              </Space>
            </Conditional>
            <Conditional if={editKey.includes(obj.key) && !isTest}>
              <Conditional if={obj.key === 'defaultPaypalCurrency'}>
                <Space alignItems="center" gap={2}>
                  <Dropdown
                    options={getDropdownOptions(obj.key)}
                    selection
                    value={
                      selectedDefaultCurrency || data.defaultPaypalCurrency
                    }
                    onChange={onDefaultCurrencyChange}
                    icon={<CustomIcon componentName="CHEVRONDOWN" />}
                  />
                  <Button
                    data-testid="confirm"
                    size="small"
                    secondary
                    onClick={handleConfirmClick(obj.key)}
                    disabled={ctaLoader === obj.key}
                    loading={ctaLoader === obj.key}
                    data-event-name="Form_UploadDocuments_Button_Confirm"
                  >
                    Confirm
                  </Button>
                </Space>
              </Conditional>
              <Conditional if={obj.key === 'activePaypalCurrencies'}>
                <div>
                  <Space
                    className="active-currency-label-wrapper mb-1"
                    alignItems="center"
                  >
                    {data.activePaypalCurrencies.map(currency => (
                      <Space
                        key={currency}
                        alignItems="center"
                        className="active-currency-label mb-1 mr-1 px-1"
                      >
                        <Text>{currency}</Text>
                        {/* eslint-disable-next-line */}
                        <div
                          data-testid="deactivate-cta"
                          className="deactivate-cta pointer ml-1"
                          onClick={deactivateCurrency(currency)}
                          data-event-name="Form_UploadDocuments_Icon_Close"
                          data-ignore-inner-text="true"
                        >
                          <CustomIcon componentName="CLOSE_SMALL" />
                        </div>
                      </Space>
                    ))}
                  </Space>
                  <Space alignItems="center" gap={2}>
                    <Dropdown
                      options={getDropdownOptions(obj.key)}
                      selection
                      value={selectedActiveCurrency}
                      onChange={onActiveCurrencyChange}
                      placeholder="Add currency"
                      icon={<CustomIcon componentName="CHEVRONDOWN" />}
                    />
                    <Button
                      size="small"
                      secondary
                      onClick={handleConfirmClick(obj.key)}
                      disabled={ctaLoader === obj.key}
                      loading={ctaLoader === obj.key}
                      data-event-name="Form_UploadDocuments_Button_Confirm"
                    >
                      Confirm
                    </Button>
                  </Space>
                </div>
              </Conditional>
            </Conditional>
          </Space>
          <Conditional if={obj.key === 'activePaypalCurrencies'}>
            <Text variant="b12" color="bodyLight">
              Only non-INR transactions are accepted via PayPal
            </Text>
          </Conditional>
        </Space>
      </div>
    ));
  };

  return (
    <div>
      <Space
        alignItems="center"
        justifyContent="space-between"
        className={expand ? 'mb-3' : ''}
      >
        <Space alignItems="center" gap={2}>
          {/* Add the logic for iconMapper from parentCode */}
          <CustomIcon componentName="PAYPAL" />
          <Text variant="b14" color="bodyLight">
            Paypal
          </Text>
        </Space>
        <Space alignItems="center">
          {/* use the accountStatus here */}
          <Conditional if={isAccountActive}>
            <Text className={isTest ? '' : 'mr-3'} color="success">
              {titleCase(accountStatus)}
            </Text>
          </Conditional>
          <Conditional if={!isTest}>
            <div data-testid="toggle">
              <Toggle
                // disabled={disablePaymentMethod}
                active={isAccountActive}
                onToggle={onToggle}
              />
            </div>
          </Conditional>
          <Conditional if={!!accountStatus}>
            <div
              data-testid="chevron"
              className="ml-7 pointer pl-1"
              onClick={onChevronClick}
              data-event-name="Form_UploadDocuments_Icon_Expand"
              data-ignore-inner-text="true"
            >
              <CustomIcon
                componentName="CHEVRONDOWN"
                direction={expand ? 'up' : 'down'}
              />
            </div>
          </Conditional>
        </Space>
      </Space>
      <Conditional if={expand}>{renderAccountDetails()}</Conditional>
      {/* Add a condition here to show the Forex markup */}
      {/* check if we need to show for gamma / test etc. */}
      <Conditional if={showForexMarkup}>
        <ForexMarkup
          markup={forexInputValue}
          setForexMarkup={setForexInputValue}
          isForexMarkupEnabled={!!isForexMarkupEnabled}
          onMarkupSubmit={onMarkupSubmit}
          onMarkupUpdate={onMarkupUpdate}
        />
      </Conditional>
      <Conditional if={openModal === ENABLE_FOREX_MARKUP}>
        <AlertModal
          type="warning"
          title={`Customers will pay a markup of ${forexInputValue}% over their transaction value`}
          onConfirm={updateForexMarkupStatus(true)}
          onClose={onForexMarkupModalClose}
          confirmText="Agree and Continue"
          eventNameOnClose="Enable_Forex_Markup_Modal_Close"
          eventNameOnConfirm="Enable_Forex_Markup_Modal_Confirm"
        >
          <Text color="bodyLight">
            Note: Cashfree will charge 1% of INR order value for such successful
            transactions
          </Text>
        </AlertModal>
      </Conditional>
      <Conditional if={openModal === DISABLE_FOREX_MARKUP}>
        <ConfirmModal
          danger
          title="Disable Forex Markup"
          confirmText="Disable"
          onClose={onForexMarkupModalClose}
          closeBtnEvent="Disable_Forex_Markup_Close"
          confirmBtnEvent="Disable_Forex_Markup_Confirm"
          onConfirm={updateForexMarkupStatus(false)}
        >
          Are you sure you want to disable the Forex Markup?
        </ConfirmModal>
      </Conditional>
    </div>
  );
};

Paypal.propTypes = {
  data: PropTypes.object,
  onToggleClick: PropTypes.func.isRequired,
  isTest: PropTypes.bool.isRequired,
  updatePaypalConfig: PropTypes.func.isRequired,
  activatePaypalCurrency: PropTypes.func.isRequired,
  deactivatePaypalCurrency: PropTypes.func.isRequired,
  updateDefaultPaypalCurrency: PropTypes.func.isRequired,
  paypalRedirectionSucess: PropTypes.bool.isRequired,
};

export default Paypal;
